import { ParsedUrlQuery } from 'querystring';

import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';

import { CloseIcon, PlaneTakeOff, SeturLogo } from '@assets/icons';
import {
  KidInterface,
  ListingSearchRoomInterface,
} from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import CustomIcon from '@components/utils/CustomIcon';
import { callService } from '@api/remote-call';
import {
  formatMomentDate,
  HOTEL_SEARCH_DATE_FORMAT,
  URL_DATE_FORMAT,
} from '@utils/dateUtil';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import clean from '@utils/cleanObject';
import { HOTEL_PAST_SEARCH } from '@constants/constants';
import {
  hotelItemsTOStringify,
  validateGuestsChildrenAges,
  generateSearchRooms,
} from '@components/views/landing/components/booker/common/bookerUtils';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import { SEARCH_LENGTH, handleFilterAndUpdateUrl } from '@utils/utils';
import { SizeUnit } from '@utils/types';
import { useDebounceValue } from '@hooks/hooks';
import { validRoomChildAge } from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveListingSearchGuest';
import { StyledDrawer } from '@components/views/components/listing/ResponsiveListingSearchStyle';
import {
  ResponsiveSearchDrawerButton,
  ResponsiveSearchSubmitButtonWrapper,
} from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveListingSearchStyle';
import ResponsiveListingSearchLocation from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveListingSearchLocation';
import ResponsiveListingSearchDate from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveListingSearchDate';
import {
  AirportSearchViewModel,
  CalendarHolidaysViewModel,
  WebUrlType,
} from '@api/v3';
import ResponsiveListingSearchGuest from '@components/views/landing/components/booker/CyprusTabContent/components/booker-search-guests/SearchGuest/ResponsiveListingSearchGuest';
import { findMatchingDateFields } from '@utils/findMatchingDateFields';
import { paramsToObject } from '@components/views/utils';
import useRouterNext from '@hooks/useRouterNext';
import { convertQueryRoomToHotelRooms } from '@components/views/hotel/utils';

import { PACKAGE_MAX_AGE } from '../package-search-bar/SearchBarInterface';
import ResponsiveAirportSearch from './AirportSearch/ResponsiveAirportSearch';

interface ResponsiveDrawerProps {
  isShowModal: boolean;
  hideModal: () => void;
  title?: ReactNode;
  drawerHeight?: SizeUnit;
  onResearchClick?: () => void;
  shouldShallowPush?: boolean;
  detail?: boolean;
  departureAirportSearchText?: string;
  onDepartureAirportSelect: (string) => void;
  departureAirport?: AirportSearchViewModel;
  togglesPackageNearestDateWarningVisible?: (value: boolean) => void;
  isAirportSelectionClicked?: boolean;
  setIsAirportSelectionClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  isAirportDateSelectionStarted?: boolean;
  setisAirportDateSelectionStarted?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsRedirecting?: React.Dispatch<React.SetStateAction<boolean>>;
  isHoliday?: boolean;
  initialData?: CalendarHolidaysViewModel;
}

const ResponsiveSearch = ({
  isShowModal,
  hideModal,
  title = <CustomIcon icon={SeturLogo} width={80} height={40} />,
  drawerHeight = 'initial',
  onResearchClick,
  detail,
  departureAirportSearchText,
  onDepartureAirportSelect,
  departureAirport,
  togglesPackageNearestDateWarningVisible,
  isAirportDateSelectionStarted,
  isAirportSelectionClicked,
  setIsAirportSelectionClicked,
  setisAirportDateSelectionStarted,
  setIsRedirecting,
  isHoliday,
  initialData,
}: ResponsiveDrawerProps) => {
  const [isShowSearchListingLocation, setIsSHowSearchListingLocation] =
    useState(false);

  const [isShowSearchListingGuest, setIsSHowSearchListingGuest] =
    useState(false);

  const [isShowSearchListingDate, setIsSHowSearchListingDate] = useState(false);
  const router = useRouter();

  const {
    hotelRoomsList,
    setHotelRoomsList,
    location,
    setLocation,
    startDate,
    endDate,
    isHotelDateSelected,
  } = useSearchBarContext();

  useEffect(() => {
    if (startDate && endDate) {
      setDateError(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (departureAirport) {
      setAirportError(false);
    }
  }, [departureAirport]);

  const [locationInputError, setLocationInputError] = useState(false);
  const [airportError, setAirportError] = useState(false);
  const [dateError, setDateError] = useState(false);

  const [hotelRooms, setHotelRooms] = useState<
    Array<ListingSearchRoomInterface>
  >([...hotelRoomsList]);

  const [inputLocation, setInputLocation] = useState<string>(
    location?.text || ''
  );
  const debounceLocation = useDebounceValue<string>(inputLocation);
  const [selectedLocation, setSelectedLocation] = useState<any>();

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const params = useParams();
  const pathname = usePathname();

  const { push } = useRouterNext();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const queryRooms = convertQueryRoomToHotelRooms(
      (query.room as string) ?? '2'
    );
    setHotelRooms(queryRooms);
    setHotelRoomsList(queryRooms);
  }, [query]);

  const clearLocation = () => {
    setInputLocation('');
    if (!detail) {
      setLocation({
        id: 0,
        text: '',
        url: '',
      });
    }
  };

  const handleUpdateFinalRooms = (
    rooms: Array<ListingSearchRoomInterface>
  ): void => {
    setHotelRoomsList(rooms);
  };

  useEffect(() => {
    if (!isHotelDateSelected) return;
    setIsSHowSearchListingDate(true);
  }, [isHotelDateSelected]);

  // ===================================
  // Search Button Clicked
  // ===================================
  const handleSearchClick = (): void => {
    if (!location.text) {
      setLocationInputError(true);
      return;
    }
    if (startDate && endDate) {
      if (!departureAirport) {
        setAirportError(true);
        return;
      }
    }
    if (departureAirport) {
      if (!startDate || !endDate) {
        setDateError(true);
        return;
      }
    }

    if (!locationInputError && !airportError && !dateError) {
      setIsRedirecting?.(true);
    }
    const packageNearestDate = findMatchingDateFields({
      in: startDate?.format(URL_DATE_FORMAT) ?? '',
      out: endDate?.format(URL_DATE_FORMAT) ?? '',
    });

    if (packageNearestDate.length > 0) {
      togglesPackageNearestDateWarningVisible?.(true);
      setIsRedirecting?.(false);

      return;
    }

    const rooms = generateSearchRooms(hotelRooms, true);
    const newQuery = clean({
      in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
      out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      room: hotelRoomsList,
      slug: params?.slug as string,
      sort: query.sort as string,
      page: query.page ? (query.page as string) : '1',
      size: query.size as string,
      isPackage: true,
      dep: departureAirport?.airportCode,
      depIsCity: departureAirport?.isCity ? 'true' : 'false',
      arr: query.arr as string,
    });

    // ===================================
    // Add new search to the local storage
    // ===================================
    const items = localStorage.getItem(HOTEL_PAST_SEARCH)
      ? JSON.parse(localStorage.getItem(HOTEL_PAST_SEARCH) || '')
      : [];

    const newSearch = clean({
      in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
      out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      rooms: hotelRoomsList,
      slug: location.url,
      isPackage: true,
      dep: departureAirport?.airportCode,
      depIsCity: departureAirport?.isCity ? 'true' : 'false',
      arr: query.arr as string,
      ...location,
    });

    if (!items.find((i: any) => i.url === location.url)) {
      items.unshift(newSearch);
    } else {
      const target = items.find((i: any) => i.url === location.url);

      const targetIndex = items.indexOf(target);
      items.splice(targetIndex, 1);
      items.unshift(newSearch);
    }

    localStorage.setItem(
      HOTEL_PAST_SEARCH,
      JSON.stringify(items, hotelItemsTOStringify)
    );

    const { ...nextQuery } = newQuery;

    hideModal();
    onResearchClick?.();
    const handleRedirect = (location) => {
      const roomUrlStr = generateSearchRooms(hotelRooms);
      const baseQuery = {
        in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) || null,
        out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) || null,
        slug: location?.url,
        room: roomUrlStr,
        isPackage: true,
        dep: departureAirport?.airportCode,
        depIsCity: departureAirport?.isCity ? 'true' : 'false',
        arr: query.arr,
      };

      const href = handleFilterAndUpdateUrl(clean(baseQuery));
      setIsRedirecting?.(false);
      router.push(`/${href}`);
    };

    const isHotelOrPackage = [
      WebUrlType.HOTEL_CITY,
      WebUrlType.HOTEL_REGION,
      WebUrlType.HOTEL_CHAIN,
      WebUrlType.HOTEL_THEME,
      WebUrlType.PACKAGE_CITY,
      WebUrlType.PACKAGE_REGION,
      WebUrlType.PACKAGE_THEME,
    ].includes(location?.type as WebUrlType);

    if (isHotelOrPackage || location?.type === WebUrlType.HOTEL) {
      if (location.redirectUrl) {
        handleRedirect(location);
      } else if (location?.type === WebUrlType.HOTEL) {
        const newQuery = {
          in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
          out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
          room: rooms,
          isPackage: true,
          dep: departureAirport?.airportCode,
          depIsCity: departureAirport?.isCity ? 'true' : 'false',
          arr: query.arr,
        };

        const newPathname = `/${location.url}`;
        const normalizeQuery = (query) => ({
          ...query,
          room: Array.isArray(query.room) ? query.room.join(',') : query.room,
          isPackage: query.isPackage ? 'true' : 'false',
        });

        const isSameUrl =
          pathname === newPathname &&
          Object.keys(normalizeQuery(newQuery)).every(
            (key) =>
              normalizeQuery(query)[key] === normalizeQuery(newQuery)[key]
          );

        if (isSameUrl) {
          setIsRedirecting?.(false);
          window.location.reload(); // TODO: Find a better solution instead of reload
        } else {
          handleRedirect(location);
        }
      } else {
        handleRedirect(location);
        clearLocation();
      }
    } else {
      setIsRedirecting?.(false);
      push({ pathname: '/', query: nextQuery });
      clearLocation();
    }
  };

  // ================================================
  // Fetch locations/hotels and fill the search by location
  // ================================================
  const fetchLocationsItems = async () => {
    return callService(
      { service: 'SearchesService', method: 'searchesPackage', version: 'v3' },
      {
        s: debounceLocation?.toLocaleLowerCase(),
      }
    );
  };

  const {
    data: locations = [],
    isError,
    isInitialLoading: isLoading,
  } = useQuery(['locationItems', debounceLocation], fetchLocationsItems, {
    enabled: Boolean(
      debounceLocation.length >= SEARCH_LENGTH && inputRef.current
    ),
  });

  // ==============================================
  // Handling the input search
  // ==============================================
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputLocation(e.target.value);
  };

  // ==============================================
  // Handling selecting an item from the autocomplete list
  // ==============================================
  const handleLocationClicked = (i: any): void => {
    setSelectedLocation(i);
    setInputLocation(i.text);
  };

  const [openAutoCompleteResult, setOpenAutoCompleteResult] =
    useState<boolean>(false);
  // ==============================================
  // Handling Clicking on the apply button
  // ==============================================
  const applySearchLocationHandler = () => {
    setLocation(selectedLocation);
    setIsSHowSearchListingLocation(false);
  };

  // ==============================================
  // Handling Clicking on close location search icon
  // ==============================================

  const handleCloseLocationSearch = (): void => {
    if (location?.text) {
      setInputLocation(location?.text);
    }
    setIsSHowSearchListingLocation(false);
    setLocation(location);
    setOpenAutoCompleteResult(false);
  };

  const clearRoomsValues = (): void => {
    setHotelRooms(hotelRoomsList);
  };

  // ============================================================
  // -Custom functions to control the children error
  // -using this function we can find an invalid child age and update te error property
  // =============================================================
  const handleChildError = (): void => {
    setHotelRooms((hr) =>
      hr?.map((room) =>
        room?.childrenList?.length !== 0
          ? {
              ...room,
              childrenList:
                room?.childrenList &&
                room?.childrenList?.map((kid: KidInterface) =>
                  validRoomChildAge(String(kid.age), PACKAGE_MAX_AGE)
                    ? { ...kid, error: false }
                    : { ...kid, error: true }
                ),
            }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to handle the close mobile guest modal
  // =============================================================
  const handleHideMobileGuestModal = () => {
    if (validateGuestsChildrenAges(hotelRooms, PACKAGE_MAX_AGE)) {
      handleChildError();
    } else {
      setIsSHowSearchListingGuest(false);
    }
  };

  // ============================================================
  // -Custom functions to handle the show mobile guest modal
  // =============================================================
  const handleShowMobileGuestModal = () => {
    clearRoomsValues();
    setIsSHowSearchListingGuest(true);
  };

  useLayoutEffect(() => {
    if (
      pathname === '/seyahat-planlama' ||
      pathname === '/profil/favorilerim'
    ) {
      clearLocation();
    }
  }, []);
  return (
    <div>
      <StyledDrawer
        title={title}
        closeIcon={
          <CustomIcon icon={CloseIcon} fontSize={24} paletteColor='black' />
        }
        placement='top'
        onClose={() => {
          hideModal();
        }}
        visible={isShowModal}
        height={drawerHeight}
        footer={
          <ResponsiveSearchSubmitButtonWrapper>
            <ResponsiveSearchDrawerButton
              variant='Primary'
              block
              onClick={handleSearchClick}
            >
              Yeniden Ara
            </ResponsiveSearchDrawerButton>
          </ResponsiveSearchSubmitButtonWrapper>
        }
      >
        <div>
          <LocationAnimationErrorBox hasError={locationInputError}>
            <ResponsiveListingSearchLocation
              showLocationModal={() => setIsSHowSearchListingLocation(true)}
              width='100%'
              height='100%'
              placement='top'
              title='Otel / Konum Arama'
              visible={isShowSearchListingLocation}
              locationInput={inputLocation}
              handleLocationInputChange={handleChange}
              autoCompleteData={locations}
              autoCompleteItemClicked={handleLocationClicked}
              locationInputError={locationInputError}
              handleUpdateButton={applySearchLocationHandler}
              isFilled={inputLocation.length > 0}
              inputRef={inputRef}
              searchTextError={isError}
              searchTextLoading={isLoading}
              handleCloseLocationSearch={handleCloseLocationSearch}
              openAutoCompleteResultModal={openAutoCompleteResult}
              setOpenAutoCompleteResultModal={setOpenAutoCompleteResult}
              locationDrawerSubTitle={location?.text || 'Otel adı veya konum'}
              type={inputLocation ? undefined : 'hotel'}
            />
          </LocationAnimationErrorBox>
          <LocationAnimationErrorBox hasError={airportError}>
            <ResponsiveAirportSearch
              onAirportSelect={onDepartureAirportSelect}
              title='Nereden Gideceksiniz?'
              subTitle={departureAirportSearchText}
              placeholder='Havalimanı veya şehir'
              drawerTitle='Uçuş Arama - Gidiş'
              icon={PlaneTakeOff}
              isPackage={true}
              isAirportDateSelectionStarted={isAirportDateSelectionStarted}
              isAirportSelectionClicked={isAirportSelectionClicked}
              setIsAirportSelectionClicked={setIsAirportSelectionClicked}
              setisAirportDateSelectionStarted={
                setisAirportDateSelectionStarted
              }
              isShowSearchListingDate={isShowSearchListingDate}
              setIsSHowSearchListingDate={setIsSHowSearchListingDate}
            />
          </LocationAnimationErrorBox>
          <LocationAnimationErrorBox hasError={dateError}>
            <ResponsiveListingSearchDate
              showDateModal={() => setIsSHowSearchListingDate(true)}
              width='100%'
              height='100%'
              title='Tarih Arama'
              placement='top'
              visible={isShowSearchListingDate}
              hideDateModal={() => {
                setIsSHowSearchListingDate(false);
              }}
              isHoliday={isHoliday}
              initialData={initialData}
              handleUpdateDateValues={() => {
                setisAirportDateSelectionStarted?.(false);
              }}
            />
          </LocationAnimationErrorBox>

          <ResponsiveListingSearchGuest
            width='100%'
            height='100%'
            placement='top'
            visible={isShowSearchListingGuest}
            showGuestModal={handleShowMobileGuestModal}
            hideGuestModal={handleHideMobileGuestModal}
            rooms={hotelRooms}
            onHotelRoomsUpdate={(hr) => setHotelRooms(hr)}
            finialRooms={hotelRoomsList}
            handleUpdateFinalRooms={handleUpdateFinalRooms}
            handleChildError={handleChildError}
            maxAdultCount={4}
          />
        </div>
      </StyledDrawer>
    </div>
  );
};

export default ResponsiveSearch;
